import React, { useEffect, useState } from 'react';
import { API } from '../api';
import { defaultUserPicture, fileToDataUrl } from '../imageUtils';

const emptyForm = {
  id: null,
  username: '',
  name: '',
  password: '',
  role: 'farmer',
  registration_number: '',
  profile_picture: ''
};

export default function Profiles({ token }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadUsers = () => {
    fetch(`${API}/users`, { headers: { Authorization: token } })
      .then((res) => res.json())
      .then(setUsers)
      .catch(() => setError('Could not load profiles.'));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(false);
    setError('');
    setMessage('');
  };

  const startEdit = (user) => {
    setForm({
      id: user.id,
      username: user.username || user.name,
      name: user.name,
      password: '',
      role: user.role,
      registration_number: user.registration_number || '',
      profile_picture: user.profile_picture || ''
    });
    setEditing(true);
    setError('');
    setMessage('');
  };

  const saveProfile = async () => {
    setError('');
    setMessage('');

    if (!form.username.trim() || !form.name.trim() || (!editing && !form.password.trim())) {
      setError(editing ? 'Username and name are required.' : 'Username, name, and password are required.');
      return;
    }

    const payload = {
      username: form.username.trim(),
      name: form.name.trim(),
      password: form.password,
      role: form.role,
      registration_number: form.registration_number.trim() || null,
      profile_picture: form.profile_picture || null
    };

    const res = await fetch(`${API}/users${editing ? `/${form.id}` : ''}`, {
      method: editing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      setError('Could not save profile. The username or registration number may already exist.');
      return;
    }

    setMessage(editing ? 'Profile updated.' : 'Profile created.');
    setForm(emptyForm);
    setEditing(false);
    loadUsers();
  };

  const setBlocked = async (user, blocked) => {
    setError('');
    setMessage('');
    const res = await fetch(`${API}/users/${user.id}/${blocked ? 'block' : 'unblock'}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({})
    });

    if (!res.ok) {
      setError(`Could not ${blocked ? 'block' : 'unblock'} profile.`);
      return;
    }

    setMessage(`${user.name} ${blocked ? 'blocked' : 'unblocked'}.`);
    loadUsers();
  };

  const farmers = users.filter((user) => user.role === 'farmer');
  const buyers = users.filter((user) => user.role === 'buyer');

  const renderUser = (user) => (
    <div className="profileRow" key={user.id}>
      <div>
        <img className={`profilePictureSmall ${user.blocked_at ? 'blockedAvatar' : ''}`} src={user.profile_picture || defaultUserPicture} alt="" />
      </div>
      <div>
        <span>{user.name}</span>
        <small>Username: {user.username || user.name}</small>
        <small>{user.registration_number || 'No registration number'}</small>
        {user.blocked_at && <small className="blockedText">Blocked</small>}
      </div>
      <div className="profileActions">
        <button className="smallButton" onClick={() => startEdit(user)}>Edit</button>
        <button className="smallButton" onClick={() => setBlocked(user, !user.blocked_at)}>
          {user.blocked_at ? 'Unblock' : 'Block'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="wideContainer">
      <h2>Profiles</h2>
      {error && <p className="error">{error}</p>}
      {message && <p className="success">{message}</p>}

      <div className="profilesLayout">
        <section className="adminPanel">
          <h3>{editing ? 'Edit Profile' : 'Create Profile'}</h3>
          <input value={form.username} onChange={(e) => updateForm('username', e.target.value)} placeholder="Username" />
          <input value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder="Actual name" />
          <input
            type="password"
            value={form.password}
            onChange={(e) => updateForm('password', e.target.value)}
            placeholder={editing ? 'New password optional' : 'Temporary password'}
          />
          <select value={form.role} onChange={(e) => updateForm('role', e.target.value)}>
            <option value="farmer">Farmer</option>
            <option value="buyer">Buyer</option>
          </select>
          <input
            value={form.registration_number}
            onChange={(e) => updateForm('registration_number', e.target.value)}
            placeholder={form.role === 'farmer' ? 'Registration number' : 'Registration number optional'}
          />
          <div className="imageField">
            <img className="imagePreview" src={form.profile_picture || defaultUserPicture} alt="" />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                if (e.target.files?.[0]) updateForm('profile_picture', await fileToDataUrl(e.target.files[0]));
              }}
            />
          </div>
          {form.profile_picture && <button className="secondaryButton" onClick={() => updateForm('profile_picture', '')}>Use Default Picture</button>}
          <button onClick={saveProfile}>{editing ? 'Save Changes' : 'Create Profile'}</button>
          {editing && <button className="secondaryButton" onClick={resetForm}>Cancel Edit</button>}
        </section>

        <section className="adminPanel">
          <h3>Farmers</h3>
          <div className="profileList">
            {farmers.map(renderUser)}
            {farmers.length === 0 && <p>No farmers yet.</p>}
          </div>
        </section>

        <section className="adminPanel">
          <h3>Buyers</h3>
          <div className="profileList">
            {buyers.map(renderUser)}
            {buyers.length === 0 && <p>No buyers yet.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
